import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, delay, map, catchError, throwError } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { ApiUrls } from '../constants/api-urls';
import { GenericResponseDto } from '../models/generic-dto.model';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: 'artist' | 'manager';
  providerId: number
}

export type LoginProvider = 'google' | 'apple' | 'microsoft' | 'credentials';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Signal-based state (primary state management)
  isAuthenticated = signal<boolean>(false);
  currentUser = signal<User | null>(null);

  // Observable versions for backward compatibility (if needed)
  isAuthenticated$ = toObservable(this.isAuthenticated);
  currentUser$ = toObservable(this.currentUser);

  constructor(private router: Router, private http: HttpClient) {
    // Check for existing session on init
    this.checkAuthStatus();
  }

  private checkAuthStatus(): void {
    // Use checkAuth() to verify authentication status
    // This avoids duplicate logic and ensures consistency
    this.checkAuth().subscribe({
      next: (isAuthenticated) => {
        // Auth status determined and state updated by checkAuth()
        // No additional action needed here
      },
      error: () => {
        // Already handled by checkAuth()
      }
    });
  }


  sendOtp(email: string): Observable<GenericResponseDto> {

    let response = this.http.post<GenericResponseDto>(ApiUrls.SEND_OTP, { email });

    return response;
  }

  login(email: string, otp: string): Observable<GenericResponseDto> {

    let response = this.http.post<GenericResponseDto>(ApiUrls.LOGIN, { email, otp });

    return response;
  }

  /**
   * Fetch current user details from /me endpoint
   */
  getCurrentUser(): Observable<User> {
    return this.http.get<GenericResponseDto>(
      ApiUrls.ME,
      { withCredentials: true }
    ).pipe(
      map(res => {
        if (res.isSucceeded) {
          const user = res.result as User;
          this.setAuthState(true, user);
          return user;
        } else {
          throw new Error('Failed to fetch user');
        }
      })
    );
  }

  /**
   * Returns an Observable that emits true/false once auth status is determined
   * Used by route guards to wait for authentication check
   */
  checkAuth(): Observable<boolean> {
    // If we already have a user in sessionStorage, return true immediately
    const userStr = sessionStorage.getItem('current_user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user) {
          // Optimistically return true, backend will verify in background
          this.setAuthState(true, user);
          return of(true);
        }
      } catch (e) {
        sessionStorage.removeItem('current_user');
      }
    }

    // No cached user - need to verify with backend
    return this.http.get<GenericResponseDto>(
      ApiUrls.ME,
      { withCredentials: true }
    ).pipe(
      map(res => {
        if (res.isSucceeded && res.result) {
          const user = res.result as User;
          this.setAuthState(true, user);
          return true;
        }
        this.clearAuthState();
        return false;
      }),
      catchError(() => {
        // Auth check failed
        this.clearAuthState();
        return of(false);
      })
    );
  }

  /**
   * Refresh the access token using the refresh token
   */
  refreshToken(): Observable<GenericResponseDto> {
    // Get email from currentUser or sessionStorage (for page refresh scenarios)
    let email = this.currentUser()?.email;
    if (!email) {
      const userStr = sessionStorage.getItem('current_user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          email = user.email;
        } catch (e) {
          // If parsing fails, email remains undefined
        }
      }
    }

    // If no email found, user never logged in - fail immediately
    if (!email) {
      return throwError(() => new Error('No user session found'));
    }

    return this.http.post<GenericResponseDto>(
      ApiUrls.REFRESH,
      { email: email },
      { withCredentials: true }
    ).pipe(
      map((response: GenericResponseDto) => {
        if (!response.isSucceeded) {
          throw new Error('Token refresh failed');
        }
        // Cookies are automatically updated by backend
        // No need to manually handle tokens
        return response;
      })
    );
  }

  /**
   * Logout the current user
   */
  logout(): void {
    // Get email BEFORE clearing state
    let email = this.currentUser()?.email;
    if (!email) {
      const userStr = sessionStorage.getItem('current_user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          email = user.email;
        } catch (e) {
          // If parsing fails, email remains undefined
        }
      }
    }

    // Clear state IMMEDIATELY - don't wait for API response
    // This prevents re-authentication during navigation
    this.clearAuthState();
    sessionStorage.removeItem('current_user');

    // Call backend logout to clear cookies (best effort)
    this.http.post<GenericResponseDto>(
      ApiUrls.LOGOUT,
      { email: email },
      { withCredentials: true }
    ).subscribe({
      next: () => {
        // Already cleared, just navigate
        this.router.navigate(['/login']);
      },
      error: () => {
        // Already cleared, just navigate
        this.router.navigate(['/login']);
      }
    });
  }

  private setAuthState(authenticated: boolean, user: User | null): void {
    this.isAuthenticated.set(authenticated);
    this.currentUser.set(user);

    // Store ONLY user info for UI display (not security-critical)
    // Use sessionStorage instead of localStorage (cleared on tab close)
    if (authenticated && user) {
      sessionStorage.setItem('current_user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('current_user');
    }
  }

  clearAuthState(): void {
    // Tokens are in HTTP-only cookies (inaccessible to JavaScript)
    // Only need to clear user info from sessionStorage
    sessionStorage.removeItem('current_user');
    this.setAuthState(false, null);
  }

}
